import { useApolloClient, useMutation } from '@apollo/client';
import { Alert, Typography } from '@mui/material';
import { ConfirmDialog } from 'components/common/confirm-dialog';
import {
  ADMIN_RESULT_COUNTS,
  PhoneNumberPicker,
  PhoneSearchCriteria,
} from 'components/nouns/phone/number-picker';
import { TelnyxQuery } from 'components/views/campaign/vendors/telnyx.graphql';
import { useSnackbar } from 'providers/snackbar';
import { FC, useState } from 'react';
import {
  ProvisionAdditionalAgentPhone,
  ProvisionAdditionalAgentPhoneVariables,
} from 'types/apollo/ProvisionAdditionalAgentPhone';
import { TelnyxSearch, TelnyxSearchVariables } from 'types/apollo/TelnyxSearch';
import { ProvisionAdditionalAgentPhoneMutation } from './campaign-agent-phones.graphql';

interface BuyPhoneDialogProps {
  open: boolean;
  campaignId: string;
  liveCount: number;
  onClose: () => void;
  onPurchased: () => void;
}

export const BuyPhoneDialog: FC<BuyPhoneDialogProps> = ({
  open,
  campaignId,
  liveCount,
  onClose,
  onPurchased,
}) => {
  const { openSnackbar } = useSnackbar();
  const client = useApolloClient();
  const [selected, setSelected] = useState('');

  const [provision, { loading }] = useMutation<
    ProvisionAdditionalAgentPhone,
    ProvisionAdditionalAgentPhoneVariables
  >(ProvisionAdditionalAgentPhoneMutation);

  const searchPhones = async (criteria: PhoneSearchCriteria) => {
    const { data } = await client.query<TelnyxSearch, TelnyxSearchVariables>({
      query: TelnyxQuery,
      variables: criteria,
      fetchPolicy: 'no-cache',
    });
    return data.telnyxSearchPhones;
  };

  const close = () => {
    setSelected('');
    onClose();
  };

  const buy = async () => {
    try {
      const { data } = await provision({
        variables: { input: { campaignId, phoneNumber: selected } },
      });
      openSnackbar({
        message: `Bought ${data?.provisionAdditionalAgentPhone.phoneNumber ?? selected}`,
        severity: 'success',
      });
      close();
      onPurchased();
    } catch (err) {
      const reason = err instanceof Error ? err.message : '';
      openSnackbar({
        message: reason ? `Could not buy the number: ${reason}` : 'Could not buy the number',
        severity: 'error',
      });
    }
  };

  return (
    <ConfirmDialog
      open={open}
      title="Buy another agent phone number"
      confirmLabel={selected ? `Buy ${selected}` : 'Buy number'}
      confirmDisabled={!selected}
      loading={loading}
      fullWidth
      maxWidth="md"
      onConfirm={buy}
      onCancel={close}
      message={
        <>
          {/* Accurate because this endpoint sets `allowMultiple`, unlike the Vendors page. */}
          {liveCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This campaign already has {liveCount} live {liveCount === 1 ? 'number' : 'numbers'}.
              Buying adds another — it does not replace them. Drain the old one separately once the
              new number is in use.
            </Alert>
          )}
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Search Telnyx and pick a number. It is purchased immediately.
          </Typography>
          <PhoneNumberPicker
            value={selected}
            onChange={setSelected}
            onSearch={searchPhones}
            showCost
            resultCounts={ADMIN_RESULT_COUNTS}
          />
        </>
      }
    />
  );
};
